/**
 * Login-map place tick. A quiet rising whistle in the spirit of the
 * sabiá-laranjeira — one sine, never a field recording, never a tax signal.
 * Unlock on a real pointerdown; skip when the operator asked for less motion.
 */

import { BRAZIL_UF_IDS, isBrazilUf } from "~/lib/domain/brazil-ufs";

/** C5 D5 E5 F5 G5. Alphabetical UF index modulo five, independent of map colour. */
const UF_CHIRP_HZ = [523.25, 587.33, 659.25, 698.46, 783.99] as const;
const HQ_HZ = 659.25;
const PEAK_GAIN = 0.0048;
const LOWPASS_HZ = 1600;

type AudioContextCtor = typeof AudioContext;
type PlaceKind = "uf" | "hq";

let audioContext: AudioContext | null = null;
let unlocked = false;
let lastKey: string | null = null;
let previousGain: GainNode | null = null;

function reducedMotion(): boolean {
  return (
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function contextConstructor(): AudioContextCtor | undefined {
  if (typeof window === "undefined") return undefined;
  return (
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
  );
}

function pitchFor(key: string, kind: PlaceKind): number {
  if (kind === "hq") return HQ_HZ;
  const index = isBrazilUf(key) ? BRAZIL_UF_IDS.indexOf(key) : 0;
  return UF_CHIRP_HZ[index % UF_CHIRP_HZ.length] ?? 659.25;
}

function envelopeMs(kind: PlaceKind): { attack: number; decay: number; release: number } {
  return kind === "hq"
    ? { attack: 16, decay: 220, release: 70 }
    : { attack: 14, decay: 150, release: 50 };
}

function fadePrevious(now: number): void {
  if (!previousGain) return;
  previousGain.gain.cancelScheduledValues(now);
  previousGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
  previousGain = null;
}

function chirp(ctx: AudioContext, frequency: number, kind: PlaceKind): void {
  const now = ctx.currentTime;
  fadePrevious(now);
  const oscillator = ctx.createOscillator();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();
  const { attack, decay, release } = envelopeMs(kind);
  const attackAt = now + attack / 1000;
  const decayAt = attackAt + decay / 1000;
  const stopAt = decayAt + release / 1000;
  const glide = kind === "hq" ? 1.5 : 1.33;

  oscillator.type = "sine";
  oscillator.frequency.setValueAtTime(frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(frequency * glide, now + 0.1);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(LOWPASS_HZ, now);
  filter.Q.setValueAtTime(0.5, now);

  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.linearRampToValueAtTime(kind === "hq" ? PEAK_GAIN * 1.15 : PEAK_GAIN, attackAt);
  gain.gain.exponentialRampToValueAtTime(0.0001, decayAt);
  gain.gain.linearRampToValueAtTime(0, stopAt);

  oscillator.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  oscillator.start(now);
  oscillator.stop(stopAt + 0.02);
  previousGain = gain;
}

export function unlockPlaceChime(): void {
  if (reducedMotion()) return;
  const Ctor = contextConstructor();
  if (!Ctor) return;
  audioContext ??= new Ctor();
  if (audioContext.state === "suspended") void audioContext.resume();
  unlocked = true;
}

export function playPlaceChime(key: string, kind: PlaceKind = "uf"): void {
  const voice = kind === "hq" ? "HQ" : `UF:${key}`;
  if (reducedMotion() || !unlocked || !audioContext) return;
  if (audioContext.state === "suspended") void audioContext.resume();
  if (audioContext.state !== "running") return;
  if (lastKey === voice) return;
  lastKey = voice;
  chirp(audioContext, pitchFor(key, kind), kind);
}

/** Test seam. Not used by the map. */
export function resetPlaceChime(): void {
  audioContext = null;
  unlocked = false;
  lastKey = null;
  previousGain = null;
}
